// components/AI/useHistorySidebarSync.js
//
// Menjaga agar sidebar riwayat (AI Coding / AI Workspace) dan sidebar navigasi
// aplikasi TIDAK PERNAH terbuka bersamaan. Dua panel 260px di layar yang sama
// menyisakan ruang terlalu sempit untuk keluaran kode dan tabel lebar.
//
// KENAPA CustomEvent, bukan prop atau context baru:
// `sidebarCollapsed` dimiliki DashboardContainer, sedangkan halaman AI dirender
// jauh di dalam <Component /> lewat peta ROUTES — tidak ada jalur prop ke sana
// tanpa membongkar routing. Codebase ini sudah memakai idiom window CustomEvent
// untuk komunikasi lintas-komponen semacam ini ('ai:goto-note', 'ai:open-note'),
// jadi pola yang sama dipakai ulang alih-alih memperkenalkan mekanisme ketiga.
//
// ARAH SINYAL (sengaja tidak simetris — lihat catatan di bawah):
//   riwayat DIBUKA        → kirim app:sidebar-collapse {collapsed:true}
//   sidebar app DIBUKA    → terima app:sidebar-state {collapsed:false} → tutup riwayat
//   riwayat DITUTUP       → TIDAK mengirim apa pun
//
// Baris terakhir itu disengaja: menutup satu panel bukan permintaan untuk
// membuka panel lain. Kalau menutup riwayat otomatis membentangkan sidebar
// navigasi, layar berubah di tempat yang tidak disentuh user — dan tidak ada
// cara menutup keduanya sekaligus.
//
// TIDAK ADA LOOP: halaman hanya bereaksi pada collapsed === false, sedangkan
// yang dikirimnya selalu collapsed === true.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useActiveDashboard } from '../../context/ActiveDashboardContext';

// Halaman → container: minta ubah keadaan sidebar navigasi.
export const SIDEBAR_SET_EVENT   = 'app:sidebar-collapse';
// Container → halaman: siaran keadaan sidebar navigasi setiap kali berubah.
export const SIDEBAR_STATE_EVENT = 'app:sidebar-state';

export function setAppSidebarCollapsed(collapsed) {
  window.dispatchEvent(new CustomEvent(SIDEBAR_SET_EVENT, { detail: { collapsed: !!collapsed } }));
}

/**
 * @param {string} myKey  key rute halaman ini ('ai_coding' / 'ai_workspace')
 * @returns {[boolean, () => void, (v:boolean) => void]}
 */
export default function useHistorySidebarSync(myKey) {
  const activeKey = useActiveDashboard();
  const isActive  = activeKey === myKey;

  // Default TERTUTUP. Dulu AIWorkspace memakai `window.innerWidth >= 1024` dan
  // AICoding memakai `true`; keduanya dibuang agar keadaan awal tidak pernah
  // melanggar aturan "tidak boleh dua-duanya terbuka".
  const [showHistory, setShowHistory] = useState(false);

  // Halaman TIDAK di-unmount saat pindah tab — DashboardContainer memakai lazy
  // mount lalu menyembunyikan dengan display:none, sehingga komponen tetap
  // hidup. Jadi "masuk halaman" TIDAK sama dengan mount, dan default state saja
  // tidak cukup: perlu transisi tidak-aktif → aktif.
  const wasActive = useRef(false);
  useEffect(() => {
    if (isActive && !wasActive.current) setShowHistory(false);
    wasActive.current = isActive;
  }, [isActive]);

  useEffect(() => {
    const onState = (e) => {
      // Hanya halaman yang sedang tampil yang boleh bereaksi. Tanpa penjaga ini
      // halaman AI lain yang masih ter-mount di balik display:none ikut
      // mengubah state-nya diam-diam.
      if (!isActive) return;
      if (e?.detail?.collapsed === false) setShowHistory(false);
    };
    window.addEventListener(SIDEBAR_STATE_EVENT, onState);
    return () => window.removeEventListener(SIDEBAR_STATE_EVENT, onState);
  }, [isActive]);

  const toggleHistory = useCallback(() => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) setAppSidebarCollapsed(true);
  }, [showHistory]);

  return [showHistory, toggleHistory, setShowHistory];
}
